import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchTenantsWithWhatsapp,
  fetchTenantWhatsappConversations,
  fetchTenantWhatsappMessages,
  sendTenantWhatsappReply,
  fetchWhatsAppConfiguration,
  fetchWhatsAppConversationDetails,
  assignAgentToConversation,
  unassignAgentFromConversation,
  markConversationAsRead,
  updateConversationStatus,
  getWhatsAppStreamUrl,
} from "../../api/centralWhatsappApi";
import { listClientUsers } from "../../api/chatbotApi";
import useAuth from "../../hooks/useAuth";
import { isPlatformAdmin } from "../../util/accessControl";
import "../Chat.css";

// ─── Constants & Icon Helpers ──────────────────────────────────────────────
const AVATAR_COLORS = ["#2563EB", "#0891B2", "#059669", "#D97706", "#7C3AED", "#BE185D", "#DC2626"];

const STATUS_FILTERS = [
  { key: "ALL", label: "All" },
  { key: "OPEN", label: "Open" },
  { key: "CLOSED", label: "Closed" },
  { key: "UNREAD", label: "Unread" },
  { key: "ASSIGNED", label: "Assigned" },
  { key: "AI", label: "AI Handled" },
  { key: "HUMAN", label: "Human" },
  { key: "ARCHIVED", label: "Archived" },
];

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);
const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const CheckIcon = ({ color = "#94A3B8" }) => (
  <svg width="14" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const DoubleCheckIcon = ({ color = "#94A3B8" }) => (
  <svg width="20" height="11" viewBox="0 0 32 16" fill="none">
    <polyline points="2 8 7 13 19 1" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="10 8 15 13 27 1" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const AlertIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const SidebarToggleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

const InboxIllustration = () => (
  <svg width="100" height="100" viewBox="0 0 120 120" fill="none">
    <rect x="15" y="25" width="90" height="70" rx="8" fill="#EFF6FF" stroke="#BFDBFE" strokeWidth="1.5" />
    <rect x="25" y="40" width="70" height="8" rx="4" fill="#BFDBFE" />
    <rect x="25" y="54" width="50" height="8" rx="4" fill="#DBEAFE" />
    <rect x="25" y="68" width="60" height="8" rx="4" fill="#DBEAFE" />
    <circle cx="85" cy="35" r="18" fill="#2563EB" />
    <path d="M78 35l5 5 9-9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SelectIllustration = () => (
  <svg width="100" height="100" viewBox="0 0 120 120" fill="none">
    <rect x="10" y="20" width="45" height="80" rx="8" fill="#EFF6FF" stroke="#BFDBFE" strokeWidth="1.5" />
    <rect x="18" y="32" width="29" height="6" rx="3" fill="#BFDBFE" />
    <rect x="18" y="44" width="21" height="6" rx="3" fill="#DBEAFE" />
    <rect x="18" y="56" width="25" height="6" rx="3" fill="#DBEAFE" />
    <rect x="18" y="68" width="18" height="6" rx="3" fill="#DBEAFE" />
    <rect x="65" y="20" width="45" height="80" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 3" />
    <circle cx="87" cy="60" r="14" fill="#BFDBFE" />
    <path d="M87 54v6l4 2" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ─── Formatters & Normalizers ──────────────────────────────────────────────
const formatMessageTime = (value) => {
  if (!value) return "";
  const utcValue = value.endsWith("Z") ? value : value + "Z";
  const date = new Date(utcValue);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "WA";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
};

const getMessageContent = (message) => {
  if (!message) return "No messages yet.";
  if (message.body) return message.body;
  if (message.content) return message.content;
  if (message.media_mime_type) return `${message.msg_type || "Media"} attachment`;
  return message.msg_type ? `${message.msg_type} message` : "Message";
};

const normalizeMessage = (message) => ({
  id: String(message.id || message.wa_message_id || Date.now()),
  type: message.direction === "outbound" || message.type === "outbound" ? "outbound" : "inbound",
  content: getMessageContent(message),
  timestamp: formatMessageTime(message.created_at || message.timestamp),
  status: message.status || undefined,
});

const normalizeConversation = (item, messages = []) => {
  const displayName = item.customer_name || item.display_name || item.profile_name || item.phone_number || item.wa_id || "WhatsApp Customer";
  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const colorIndex = Number(item.id || 0) % AVATAR_COLORS.length;

  return {
    id: String(item.id),
    rawId: item.id,
    customerId: item.customer_id || item.id,
    customerName: displayName,
    profileName: item.profile_name || displayName,
    whatsappNumber: item.phone_number || item.customer_phone || item.wa_id || "",
    avatarInitials: getInitials(displayName),
    avatarColor: AVATAR_COLORS[colorIndex],
    lastMessage: item.last_message_preview || getMessageContent(latestMessage),
    timestamp: formatMessageTime(item.last_activity || item.last_msg_at || latestMessage?.created_at || item.created_at),
    unread: item.unread_count ?? item.unread ?? 0,
    status: (item.conversation_status || (item.active === false ? "CLOSED" : "OPEN")).toUpperCase(),
    assignedAgent: item.assigned_agent || null,
    channel: item.channel || "whatsapp",
    createdAt: item.created_at,
    lastActivity: item.last_activity || item.last_msg_at,
    language: item.language || "English (en)",
    notes: item.notes || null,
    messages: messages.map(normalizeMessage),
  };
};

// ─── Sub-Components ────────────────────────────────────────────────────────
function Avatar({ initials, color, size = 40 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color + "20",
        border: `1.5px solid ${color}40`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: size * 0.34,
        fontWeight: 600,
        color: color,
        letterSpacing: "0.02em",
        userSelect: "none",
      }}
    >
      {initials}
    </div>
  );
}

function SkeletonItem() {
  return (
    <div style={{ padding: "14px 16px", display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div className="skeleton" style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="skeleton" style={{ width: "60%", height: 13 }} />
        <div className="skeleton" style={{ width: "40%", height: 11 }} />
      </div>
    </div>
  );
}

function MessageStatusIcon({ status }) {
  if (!status) return null;
  if (status === "pending") return <span style={{ color: "#94A3B8", display: "flex", alignItems: "center" }}><ClockIcon /></span>;
  if (status === "failed") return <span style={{ color: "#DC2626", display: "flex", alignItems: "center" }}><AlertIcon /></span>;
  if (status === "sent") return <span style={{ display: "flex", alignItems: "center" }}><CheckIcon color="#94A3B8" /></span>;
  if (status === "delivered") return <span style={{ display: "flex", alignItems: "center" }}><DoubleCheckIcon color="#94A3B8" /></span>;
  return <span style={{ display: "flex", alignItems: "center" }}><DoubleCheckIcon color="#2563EB" /></span>;
}

function MessageBubble({ message }) {
  const isOut = message.type === "outbound";
  return (
    <div style={{ display: "flex", flexDirection: isOut ? "row-reverse" : "row", alignItems: "flex-end", gap: 10, marginBottom: 12 }}>
      {!isOut && (
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#E0E7FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#4338CA", flexShrink: 0 }}>
          {message.id.slice(-1).toUpperCase()}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", alignItems: isOut ? "flex-end" : "flex-start", maxWidth: "72%" }}>
        <div style={{ padding: "10px 14px", borderRadius: isOut ? "14px 14px 4px 14px" : "14px 14px 14px 4px", background: isOut ? "#2563EB" : "#F1F5F9", color: isOut ? "#FFFFFF" : "#0F172A", fontSize: 14, lineHeight: 1.6, boxShadow: isOut ? "0 1px 4px rgba(37,99,235,0.2)" : "0 1px 3px rgba(0,0,0,0.04)", wordBreak: "break-word" }}>
          {message.content}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: 11, color: "#94A3B8" }}>
          <span>{message.timestamp}</span>
          {isOut && <MessageStatusIcon status={message.status} />}
        </div>
      </div>
    </div>
  );
}

// ─── Header Account Health Badge Component ─────────────────────────────────
function AccountHealthCard({ config, tenantName }) {
  if (!config) {
    return (
      <div style={{ background: "white", padding: "10px 16px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>WhatsApp Account: {tenantName || "Loading..."}</span>
      </div>
    );
  }

  const health = (config.health || config.connection_status || "healthy").toLowerCase();
  const healthBadgeMap = {
    healthy: { bg: "#DCFCE7", text: "#15803D", label: "✓ Healthy" },
    connected: { bg: "#DCFCE7", text: "#15803D", label: "✓ Healthy" },
    degraded: { bg: "#FEF3C7", text: "#B45309", label: "⚠ Degraded" },
    unhealthy: { bg: "#FEE2E2", text: "#B91C1C", label: "✕ Unhealthy" },
  };
  const badge = healthBadgeMap[health] || healthBadgeMap.healthy;

  return (
    <div style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", padding: "10px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyBetween: "space-between", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#25D366", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold" }}>
          WA
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
            {config.business_name || config.verified_name || "DocLift WhatsApp Support"}
            <span style={{ background: badge.bg, color: badge.text, padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
              {badge.label}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
            {config.phone_number || config.display_phone_number || "No phone number"} • Webhook: <span style={{ fontWeight: 600, color: "#0F172A" }}>{config.webhook_status || "subscribed"}</span>
          </div>
        </div>
      </div>
      <div style={{ marginLeft: "auto", fontSize: 11, color: "#64748B", textAlign: "right" }}>
        <div>Sync: <span style={{ fontWeight: 500, color: "#334155" }}>{formatDateTime(config.last_sync || config.last_health_check_at)}</span></div>
        {config.processing_stage && <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>Stage: {config.processing_stage}</div>}
      </div>
    </div>
  );
}

// ─── Reply Composer ────────────────────────────────────────────────────────
function ReplyComposer({ onSend, disabled = false, noConversation = false, value, onChange }) {
  const [composerState, setComposerState] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const textareaRef = useRef(null);

  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(Math.max(el.scrollHeight, 44), 132) + "px";
  };
  useEffect(() => {
    autoGrow();
  }, [value]);

  const handleSend = async () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || noConversation) return;
    setComposerState("sending");
    setErrorMsg("");
    try {
      await onSend(trimmed);
      onChange("");
      setComposerState("idle");
      textareaRef.current?.focus();
    } catch (error) {
      setComposerState("error");
      setErrorMsg(error.message || "Failed to send message.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ borderTop: "1px solid #E2E8F0", padding: "12px 16px 16px", background: "white" }}>
      {errorMsg && <div style={{ color: "#DC2626", fontSize: 12, marginBottom: 8 }}>{errorMsg}</div>}
      <div style={{ border: "1.5px solid #E2E8F0", borderRadius: 12, background: disabled ? "#F8FAFC" : "white", overflow: "hidden" }}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || noConversation}
          placeholder={noConversation ? "Select a conversation..." : "Type a WhatsApp message..."}
          style={{ width: "100%", border: "none", padding: "12px", fontSize: 14, resize: "none", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px", background: "#FAF5FF", borderTop: "1px solid #F1F5F9" }}>
          <button
            onClick={handleSend}
            disabled={disabled || noConversation || !value.trim() || composerState === "sending"}
            style={{ background: "#2563EB", color: "white", border: "none", padding: "6px 16px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}
          >
            {composerState === "sending" ? "Sending..." : <><SendIcon /> Send</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 12-Field Conversation Detail Sidebar ──────────────────────────────────
function ConversationDetailSidebar({
  conv,
  details,
  tenantId,
  tenantUsers,
  onStatusChange,
  onAssignChange,
  onUnassign,
  onMarkRead,
  onClose,
  isTablet,
  isMobile,
}) {
  const [updating, setUpdating] = useState(false);

  const effectiveDetails = details || {};
  const customerName = effectiveDetails.customer_name || conv?.customerName || "—";
  const customerPhone = effectiveDetails.customer_phone || effectiveDetails.phone_number || conv?.whatsappNumber || "—";
  const profileName = effectiveDetails.profile_name || conv?.profileName || customerName;
  const language = effectiveDetails.language || conv?.language || "English (en)";
  const customerSince = formatDateTime(effectiveDetails.customer_since || effectiveDetails.created_at || conv?.createdAt);
  const lastActivity = formatDateTime(effectiveDetails.last_activity || conv?.lastActivity);
  const notes = effectiveDetails.notes || conv?.notes || "No notes attached.";
  const status = (effectiveDetails.conversation_status || conv?.status || "OPEN").toUpperCase();
  const assignedAgent = effectiveDetails.assigned_agent || effectiveDetails.assigned_user || conv?.assignedAgent || null;
  const whatsappAccount =
    typeof effectiveDetails.whatsapp_account === "object" && effectiveDetails.whatsapp_account
      ? effectiveDetails.whatsapp_account.display_phone_number || effectiveDetails.whatsapp_account.waba_id || "Primary WABA"
      : effectiveDetails.whatsapp_account || conv?.whatsappNumber || "Primary WABA";
  const latestMessage =
    typeof effectiveDetails.latest_message === "object" && effectiveDetails.latest_message
      ? effectiveDetails.latest_message.body || effectiveDetails.latest_message.text || "—"
      : effectiveDetails.latest_message || effectiveDetails.last_message_preview || conv?.lastMessage || "—";
  const unreadCount = effectiveDetails.unread_count ?? conv?.unread ?? 0;

  const handleStatusSelect = async (newStatus) => {
    setUpdating(true);
    try {
      await onStatusChange(newStatus);
    } finally {
      setUpdating(false);
    }
  };

  const handleAgentSelect = async (e) => {
    const val = e.target.value;
    setUpdating(true);
    try {
      if (!val) {
        await onUnassign();
      } else {
        await onAssignChange(val);
      }
    } finally {
      setUpdating(false);
    }
  };

  const contentNode = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflowY: "auto" }}>
      {/* Mobile Drag Handle Bar */}
      {isMobile && (
        <div style={{ width: 40, height: 5, background: "#CBD5E1", borderRadius: 99, margin: "0 auto 12px" }} />
      )}

      {/* Sidebar Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F1F5F9", paddingBottom: 12, marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Customer Details</h3>
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748B" }}>12 system metadata fields</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            title="Close Details"
            style={{
              background: "#F1F5F9",
              border: "none",
              borderRadius: "50%",
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#475569",
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            ×
          </button>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, fontSize: 12 }}>
        {/* Field 1: Customer Name */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>1. Customer Name</span>
          <div style={{ fontWeight: 600, color: "#0F172A", marginTop: 2, fontSize: 13 }}>{customerName}</div>
        </div>

        {/* Field 2: Customer Phone */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>2. Customer Phone</span>
          <div style={{ fontWeight: 600, color: "#0F172A", marginTop: 2 }}>{customerPhone}</div>
        </div>

        {/* Field 3: Profile Name */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>3. Profile Name</span>
          <div style={{ color: "#334155", marginTop: 2 }}>{profileName}</div>
        </div>

        {/* Field 4: Language */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>4. Language</span>
          <div style={{ color: "#334155", marginTop: 2 }}>{language}</div>
        </div>

        {/* Field 5: Customer Since */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>5. Customer Since</span>
          <div style={{ color: "#334155", marginTop: 2 }}>{customerSince}</div>
        </div>

        {/* Field 6: Last Activity */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>6. Last Activity</span>
          <div style={{ color: "#334155", marginTop: 2 }}>{lastActivity}</div>
        </div>

        {/* Field 7: Notes */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>7. Notes</span>
          <div style={{ color: "#475569", marginTop: 2, background: "#F8FAFC", padding: "8px", borderRadius: 6, border: "1px solid #F1F5F9", fontStyle: "italic" }}>
            {notes}
          </div>
        </div>

        {/* Field 8: Status & Action Selector */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>8. Status</span>
          <div style={{ marginTop: 4 }}>
            <select
              value={status}
              disabled={updating}
              onChange={(e) => handleStatusSelect(e.target.value)}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #CBD5E1", background: "#F8FAFC", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              <option value="OPEN">OPEN</option>
              <option value="CLOSED">CLOSED</option>
              <option value="ARCHIVED">ARCHIVED</option>
            </select>
          </div>
        </div>

        {/* Field 9: Assigned Agent & Selector */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>9. Assigned Agent</span>
          <div style={{ marginTop: 4 }}>
            <select
              value={assignedAgent?.id || ""}
              disabled={updating}
              onChange={handleAgentSelect}
              style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #CBD5E1", background: "#F8FAFC", fontSize: 12, cursor: "pointer" }}
            >
              <option value="">Unassigned</option>
              {tenantUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email || `Agent #${u.id}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Field 10: WhatsApp Account */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>10. WhatsApp Account</span>
          <div style={{ color: "#334155", marginTop: 2 }}>{whatsappAccount}</div>
        </div>

        {/* Field 11: Latest Message Preview */}
        <div>
          <span style={{ color: "#64748B", fontWeight: 500 }}>11. Latest Message Preview</span>
          <div style={{ color: "#334155", marginTop: 2, background: "#F1F5F9", padding: "6px 8px", borderRadius: 6, fontSize: 11, wordBreak: "break-word" }}>
            {latestMessage}
          </div>
        </div>

        {/* Field 12: Unread Count & Reset Action */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "#64748B", fontWeight: 500 }}>12. Unread Count</span>
            {unreadCount > 0 && (
              <button
                onClick={onMarkRead}
                style={{ background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE", borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 600, cursor: "pointer" }}
              >
                Mark Read
              </button>
            )}
          </div>
          <div style={{ fontWeight: 700, color: unreadCount > 0 ? "#2563EB" : "#64748B", marginTop: 2 }}>
            {unreadCount} unread
          </div>
        </div>
      </div>
    </div>
  );

  // If on Tablet or Mobile: Render as Backdrop Overlay Modal / Drawer
  if (isTablet) {
    return (
      <>
        {/* Backdrop Overlay */}
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(3px)",
            zIndex: 9998,
          }}
        />
        {/* Modal Sheet / Drawer */}
        <div
          style={
            isMobile
              ? {
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                maxHeight: "85vh",
                background: "white",
                borderRadius: "20px 20px 0 0",
                zIndex: 9999,
                overflowY: "auto",
                padding: "16px 20px 24px",
                boxShadow: "0 -10px 40px rgba(0,0,0,0.2)",
                boxSizing: "border-box",
              }
              : {
                position: "fixed",
                top: 58,
                right: 0,
                bottom: 0,
                width: 360,
                background: "white",
                zIndex: 9999,
                overflowY: "auto",
                padding: "20px",
                boxShadow: "-10px 0 40px rgba(0,0,0,0.15)",
                boxSizing: "border-box",
              }
          }
        >
          {contentNode}
        </div>
      </>
    );
  }

  // Desktop View: Right Column Sidebar
  return (
    <div style={{ width: isMobile ? "100%" : 320, flexShrink: 0, background: "white", borderRight: "1px solid #E2E8F0", display: "flex", flexDirection: "column", height: "100%", overflowY: "auto", padding: "16px", boxSizing: "border-box" }}>
      {contentNode}
    </div>
  );
}

// ─── Main CentralWhatsAppInbox Component ───────────────────────────────────
export default function CentralWhatsAppInbox() {
  const { tenantId: routeTenantId, conversationId: routeConvId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const routeConvIdRef = useRef(routeConvId);
  useEffect(() => {
    routeConvIdRef.current = routeConvId;
  }, [routeConvId]);

  const isSuperAdmin = isPlatformAdmin(user);
  const userTenantId = user?.tenantId || null;

  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(
    () => routeTenantId || (!isSuperAdmin && userTenantId ? userTenantId : null)
  );

  const [waConfig, setWaConfig] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(routeConvId || null);
  const [selectedConvDetails, setSelectedConvDetails] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);

  const [filter, setFilter] = useState("ALL");
  const [convSearch, setConvSearch] = useState("");

  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [convsLoading, setConvsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [drafts, setDrafts] = useState({});
  const [composerValue, setComposerValue] = useState("");
  const [toastPopup, setToastPopup] = useState(null);
  const [showRightSidebar, setShowRightSidebar] = useState(false);

  const [windowWidth, setWindowWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;

  const messagesEndRef = useRef(null);

  // Request browser notification permission on mount
  useEffect(() => {
    if (window.Notification && Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, []);

  // Load the bots authorized for the current user.
  useEffect(() => {
    let active = true;
    setTenantsLoading(true);

    fetchTenantsWithWhatsapp().then((data) => {
      if (!active) return;
      setTenants(data);
      setTenantsLoading(false);

      setSelectedTenantId((currentId) => {
        if (data.length === 0) return null;

        const savedTenantId = localStorage.getItem("tenant_id") || userTenantId;
        const matchedTenant =
          (routeTenantId && data.find((bot) => String(bot.id) === String(routeTenantId))) ||
          (currentId && data.find((bot) => String(bot.id) === String(currentId))) ||
          (savedTenantId && data.find((bot) => String(bot.id) === String(savedTenantId))) ||
          data.find((bot) => bot.unread > 0) ||
          data.find((bot) => bot.status === "connected") ||
          data[0];

        return matchedTenant?.id || null;
      });
    });

    return () => {
      active = false;
    };
  }, [userTenantId, routeTenantId]);

  // Load WhatsApp config, users, and conversations when tenant selection, filter, or search changes
  const loadTenantData = useCallback(
    async (preferredConvId = null, showLoader = false) => {
      if (!selectedTenantId) return;

      fetchWhatsAppConfiguration(selectedTenantId).then(setWaConfig);

      if (showLoader) {
        setConvsLoading(true);
      }
      try {
        const result = await fetchTenantWhatsappConversations(selectedTenantId, {
          filter,
          search: convSearch,
        });
        const items = result.items || [];
        const normalizedList = items.map((c) => normalizeConversation(c, []));
        setConversations(normalizedList);

        // Auto-select the conversation if conversations exist
        if (normalizedList.length > 0) {
          setSelectedConvId((currentConvId) => {
            const initialRouteId = routeConvIdRef.current;
            if (preferredConvId && normalizedList.some((c) => String(c.id) === String(preferredConvId))) {
              return String(preferredConvId);
            }
            if (initialRouteId && normalizedList.some((c) => String(c.id) === String(initialRouteId))) {
              return String(initialRouteId);
            }
            if (currentConvId && normalizedList.some((c) => String(c.id) === String(currentConvId))) {
              return String(currentConvId);
            }
            return normalizedList[0].id;
          });
        } else {
          setSelectedConvId(null);
        }
      } catch (err) {
        console.error("Failed to load conversations:", err);
      } finally {
        setConvsLoading(false);
      }
    },
    [selectedTenantId, filter, convSearch]
  );

  useEffect(() => {
    if (!selectedTenantId) {
      setTenantUsers([]);
      return;
    }

    const selectedBot = tenants.find((bot) => String(bot.id) === String(selectedTenantId));
    if (!selectedBot?.clientId) {
      setTenantUsers([]);
      return;
    }

    listClientUsers(selectedBot.clientId)
      .then((res) => setTenantUsers(Array.isArray(res) ? res : res?.users || res?.items || res?.data || []))
      .catch(() => setTenantUsers([]));
  }, [selectedTenantId, tenants]);

  useEffect(() => {
    loadTenantData(null, true);
  }, [loadTenantData]);

  // Load messages and full 12-field details when conversation changes
  const loadConversationData = useCallback(async () => {
    if (!selectedTenantId || !selectedConvId) {
      setSelectedConvDetails(null);
      setMessages([]);
      return;
    }

    setMessagesLoading(true);
    fetchWhatsAppConversationDetails(selectedTenantId, selectedConvId)
      .then(setSelectedConvDetails)
      .catch(() => setSelectedConvDetails(null));

    try {
      const rawMessages = await fetchTenantWhatsappMessages(selectedTenantId, selectedConvId);
      const normalized = Array.isArray(rawMessages) ? rawMessages.map(normalizeMessage) : [];
      setMessages(normalized);

      // Also ensure conversations list reflects read status & latest message
      setConversations((prev) =>
        prev.map((c) =>
          String(c.id) === String(selectedConvId)
            ? { ...c, unread: 0, ...(normalized.length > 0 ? { lastMessage: normalized[normalized.length - 1].content } : {}) }
            : c
        )
      );
    } catch (err) {
      console.error("Failed to load conversation messages:", err);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [selectedTenantId, selectedConvId]);

  useEffect(() => {
    loadConversationData();
  }, [loadConversationData]);

  // SSE Stream Listener
  useEffect(() => {
    if (!selectedTenantId) return undefined;

    const streamUrl = getWhatsAppStreamUrl(selectedTenantId);
    const eventSource = new EventSource(streamUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // console.log("⚡ [SSE Stream Event]:", data);

        if (data.event === "connected") {
          // console.log("[SSE Stream]: Connection established for tenant", selectedTenantId);
        } else if (
          data.event === "inbound_message" ||
          data.event === "conversation_updated" ||
          data.event === "message_status_updated"
        ) {
          const incomingConvId = data.data?.conversation_id ? String(data.data.conversation_id) : null;
          const incomingTenantId = data.data?.tenant_id || data.data?.tenantId;

          if (!incomingTenantId || incomingTenantId === selectedTenantId) {
            loadTenantData(incomingConvId);
            if (selectedConvId) {
              loadConversationData();
            }
          } else if (isSuperAdmin) {
            fetchTenantsWithWhatsapp().then(setTenants);
          }

          if (data.event === "inbound_message") {
            const body = data.data?.body || data.data?.text || "New incoming WhatsApp message";
            const sender = data.data?.sender_name || data.data?.customer_name || "Customer";

            setToastPopup({
              title: `💬 New Message from ${sender}`,
              body,
              convId: incomingConvId,
              tenantId: incomingTenantId || selectedTenantId,
            });
            setTimeout(() => setToastPopup(null), 8000);

            if (window.Notification && Notification.permission === "granted") {
              new Notification(`💬 New Message from ${sender}`, {
                body,
                icon: "/logo192.png",
              });
            }
          }
        }
      } catch (err) {
        // console.error("[SSE Stream] Parse error:", err);
      }
    };

    return () => {
      eventSource.close();
    };
  }, [selectedTenantId, selectedConvId, loadTenantData, loadConversationData, isSuperAdmin]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, selectedConvId]);

  const handleSelectTenant = (id) => {
    setSelectedTenantId(id);
    setSelectedConvId(null);
    setComposerValue("");
    navigate(`/whatsapp-inbox/${id}`);
  };

  const handleSelectConv = (id) => {
    const strId = String(id);
    if (selectedConvId) {
      setDrafts((prev) => ({ ...prev, [selectedConvId]: composerValue }));
    }
    setSelectedConvId(strId);
    setComposerValue(drafts[strId] || "");
    markConversationAsRead(selectedTenantId, strId);
    navigate(`/whatsapp-inbox/${selectedTenantId}/${strId}`, { replace: true });
  };

  const handleSendReply = async (text) => {
    if (!selectedTenantId || !selectedConvId) return;
    const tempId = `pending-${Date.now()}`;
    const optimisticMsg = {
      id: tempId,
      type: "outbound",
      content: text,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      status: "pending",
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    setConversations((prev) =>
      prev.map((c) =>
        String(c.id) === String(selectedConvId)
          ? { ...c, lastMessage: text, timestamp: "just now" }
          : c
      )
    );

    try {
      const returnedMessage = await sendTenantWhatsappReply(selectedTenantId, selectedConvId, text);
      const normalized = normalizeMessage(returnedMessage);

      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? normalized : m))
      );
    } catch (error) {
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, status: "failed" } : m))
      );
      throw error;
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTenantId || !selectedConvId) return;
    await updateConversationStatus(selectedTenantId, selectedConvId, newStatus);
    loadConversationData();
  };

  const handleAssignChange = async (userId) => {
    if (!selectedTenantId || !selectedConvId) return;
    await assignAgentToConversation(selectedTenantId, selectedConvId, userId);
    loadConversationData();
  };

  const handleUnassign = async () => {
    if (!selectedTenantId || !selectedConvId) return;
    await unassignAgentFromConversation(selectedTenantId, selectedConvId);
    loadConversationData();
  };

  const handleMarkRead = async () => {
    if (!selectedTenantId || !selectedConvId) return;
    await markConversationAsRead(selectedTenantId, selectedConvId);
    loadConversationData();
  };

  const selectedTenant = tenants.find((t) => String(t.id) === String(selectedTenantId));
  const selectedConv = conversations.find((c) => String(c.id) === String(selectedConvId)) || null;

  return (
    <div style={{ display: "flex", height: "100vh", paddingTop: "58px", boxSizing: "border-box", background: "#F8FAFC", fontFamily: "Inter, sans-serif" }}>
      {/* ── 2. CONVERSATIONS PANEL ── */}
      <div style={{ width: isMobile ? "100%" : 340, background: "white", borderRight: "1px solid #E2E8F0", display: isMobile && (!selectedTenantId || selectedConvId) ? "none" : "flex", flexDirection: "column" }}>
        {/* Header with Search & Filter Tabs */}
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #F1F5F9" }}>
          {tenantsLoading || tenants.length > 0 ? (
            <label style={{ display: "block", marginBottom: 10 }}>
              <span style={{ display: "block", marginBottom: 5, color: "#64748B", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>My Bots</span>
              <select
                aria-label="Select bot"
                value={selectedTenantId || ""}
                onChange={(e) => handleSelectTenant(e.target.value)}
                disabled={tenantsLoading || tenants.length === 0}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #E2E8F0", background: "white", color: "#0F172A", fontSize: 13, fontWeight: 600 }}
              >
                {tenantsLoading ? <option value="">Loading clients...</option> : null}
                {!tenantsLoading && tenants.length === 0 ? <option value="">No clients available</option> : null}
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                ))}
              </select>
            </label>
          ) : null}
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", color: "#0F172A" }}>
            {selectedTenant ? `${selectedTenant.name} Chats` : "Conversations"}
          </h2>
          <div style={{ position: "relative", marginBottom: 10 }}>
            <span style={{ position: "absolute", left: 10, top: 8, color: "#94A3B8" }}><SearchIcon /></span>
            <input
              placeholder="Search name, phone, or ID..."
              value={convSearch}
              onChange={(e) => setConvSearch(e.target.value)}
              disabled={!selectedTenantId}
              style={{ width: "100%", padding: "7px 12px 7px 30px", borderRadius: 8, border: "1px solid #E2E8F0", boxSizing: "border-box", fontSize: 12 }}
            />
          </div>

          {/* Status Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1.5 pt-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all duration-150 shrink-0 cursor-pointer ${filter === f.key
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-600/30 ring-1 ring-blue-600"
                    : "bg-slate-50 text-slate-600 border border-slate-200/90 hover:bg-slate-100 hover:text-slate-900 hover:border-slate-300"
                  }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <div style={{ flex: 1, overflowY: "auto", background: "#F8FAFC" }}>
          {!selectedTenantId ? (
            <div style={{ padding: 30, textAlign: "center" }}>
              <SelectIllustration />
              <p style={{ marginTop: 12, color: "#64748B", fontSize: 13 }}>Select a tenant to view conversations.</p>
            </div>
          ) : convsLoading ? (
            Array.from({ length: 4 }).map((_, i) => <SkeletonItem key={i} />)
          ) : conversations.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center" }}>
              <InboxIllustration />
              <p style={{ marginTop: 12, color: "#64748B", fontSize: 13 }}>No conversations found for filter '{filter}'.</p>
            </div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => handleSelectConv(c.id)}
                style={{
                  width: "100%",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  padding: "12px 14px",
                  background: String(selectedConvId) === String(c.id) ? "#EFF6FF" : c.unread > 0 ? "#F8FAFF" : "transparent",
                  borderLeft: String(selectedConvId) === String(c.id) ? "3px solid #0891B2" : "3px solid transparent",
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <Avatar initials={c.avatarInitials} color={c.avatarColor} size={36} />
                  {c.unread > 0 && <span style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: "50%", background: "#2563EB", border: "2px solid white" }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: c.unread > 0 ? 700 : 600, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.customerName}
                    </span>
                    <span style={{ fontSize: 10, color: c.unread > 0 ? "#2563EB" : "#94A3B8", fontWeight: c.unread > 0 ? 700 : 500 }}>
                      {c.timestamp}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: c.unread > 0 ? "#0F172A" : "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.lastMessage}
                  </div>
                  {c.assignedAgent && (
                    <div style={{ fontSize: 10, color: "#0891B2", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                      <UserIcon /> {c.assignedAgent.name || c.assignedAgent.email}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── 3. CUSTOMER DETAILS (LEFT OF CHAT) ── */}
      {selectedConv && showRightSidebar && (
        <ConversationDetailSidebar
          conv={selectedConv}
          details={selectedConvDetails}
          tenantId={selectedTenantId}
          tenantUsers={tenantUsers}
          onStatusChange={handleStatusChange}
          onAssignChange={handleAssignChange}
          onUnassign={handleUnassign}
          onMarkRead={handleMarkRead}
          onClose={() => setShowRightSidebar(false)}
          isTablet={isTablet}
          isMobile={isMobile}
        />
      )}

      {/* ── 4. CHAT WINDOW & HEADER CARD ── */}
      <div style={{ flex: 1, display: isMobile && (!selectedConvId || showRightSidebar) ? "none" : "flex", flexDirection: "column", background: "#F8FAFC", minWidth: 0 }}>
        {/* Account Status Header Card */}
        <AccountHealthCard config={waConfig} tenantName={selectedTenant?.name} />

        {!selectedConv ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <SelectIllustration />
            <h3 style={{ marginTop: 16, color: "#0F172A", fontWeight: 600 }}>Select a Conversation</h3>
            <p style={{ color: "#64748B", fontSize: 13 }}>Choose a customer conversation to start responding.</p>
          </div>
        ) : (
          <>
            {/* Conversation Header */}
            <div style={{ padding: "12px 20px", background: "white", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {isMobile && (
                  <button
                    onClick={() => handleSelectConv(null)}
                    style={{ background: "none", border: "none", color: "#2563EB", fontSize: 16, fontWeight: 700, cursor: "pointer", padding: "0 6px 0 0" }}
                    title="Back to conversations"
                  >
                    ←
                  </button>
                )}
                <Avatar initials={selectedConv.avatarInitials} color={selectedConv.avatarColor} size={38} />
                <div>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "#0F172A" }}>{selectedConv.customerName}</h3>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748B" }}>
                    {selectedConv.whatsappNumber} • Status: <span style={{ fontWeight: 600, color: selectedConv.status === "OPEN" ? "#16A34A" : "#64748B" }}>{selectedConv.status}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRightSidebar((v) => !v)}
                title="Toggle Conversation Details"
                style={{ background: showRightSidebar ? "#EFF6FF" : "transparent", color: "#2563EB", border: "1px solid #BFDBFE", padding: "6px 10px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600 }}
              >
                <SidebarToggleIcon /> Details
              </button>
            </div>

            {/* Message Thread */}
            <div style={{ flex: 1, padding: "20px", overflowY: "auto" }}>
              {messagesLoading ? (
                <div style={{ textAlign: "center", padding: 20, color: "#64748B", fontSize: 13 }}>Loading messages...</div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#64748B", fontSize: 13 }}>
                  No messages in this conversation yet.
                </div>
              ) : (
                messages.map((m) => <MessageBubble key={m.id} message={m} />)
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Composer */}
            <ReplyComposer
              value={composerValue}
              onChange={setComposerValue}
              onSend={handleSendReply}
              disabled={selectedConv.status === "CLOSED"}
            />
          </>
        )}
      </div>

      {/* ── 5. REAL-TIME SSE TOAST NOTIFICATION ── */}
      {toastPopup && (
        <div
          onClick={() => {
            if (toastPopup.tenantId && toastPopup.tenantId !== selectedTenantId) {
              setSelectedTenantId(toastPopup.tenantId);
            }
            if (toastPopup.convId) {
              handleSelectConv(toastPopup.convId);
            }
            setToastPopup(null);
          }}
          style={{
            position: "fixed",
            top: "24px",
            right: "24px",
            zIndex: 9999,
            background: "#0F172A",
            color: "#FFFFFF",
            padding: "16px 20px",
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            border: "1px solid #3B82F6",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: "300px",
            cursor: "pointer",
            transition: "transform 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          <div style={{ fontSize: "24px" }}>💬</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#60A5FA" }}>{toastPopup.title}</div>
            <div style={{ fontSize: "13px", color: "#E2E8F0", marginTop: "2px" }}>{toastPopup.body}</div>
            <div style={{ fontSize: "11px", color: "#93C5FD", marginTop: "4px" }}>Click to open conversation →</div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setToastPopup(null);
            }}
            style={{ background: "none", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: "18px", padding: "0 4px" }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
